/* eslint-disable */
// src/services/localMachineService.js

import axios from "services/axiosConfig";
import { formatFormulaLabel } from "utils/formula";

const API_URL = "/api/localMachine";

/** Convierte valores 1/"1"/"true"/true a boolean real */
const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (v == null) return false;
  const n = Number(v);
  if (!Number.isNaN(n)) return n === 1;
  const s = String(v).trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
};

/** Normaliza cada fila devolviendo:
 * - IsAccumulative en boolean
 * - Id y LocalMachineId numéricos
 * - MachineId/LocalId/FormulaId/RentId numéricos (o null)
 * - Respeta objetos anidados (Machine, MachineType, Formula, Rent) si están presentes
 */
const normalizeRow = (row = {}) => {
  const id = Number(row.LocalMachineId ?? row.Id);
  const toNum = (v) => (v == null ? null : Number(v));
  const toOptionalNum = (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const machineType = row.MachineType || {};
  const formula = row.Formula || {};
  const rent = row.Rent || {};

  const machineTypeId = row.MachineTypeId ?? machineType.Id ?? null;

  return {
    ...row,
    Id: id,
    LocalMachineId: id,
    MachineId: toNum(row.MachineId ?? row.Machine?.Id),
    LocalId: toNum(row.LocalId),
    MachineTypeId: toNum(machineTypeId),
    FormulaId: toNum(row.FormulaId ?? formula.Id),
    FormulaType: row.FormulaType ?? formula.Type ?? "",
    FormulaValue: row.FormulaValue ?? formula.Value ?? 0,
    FormulaName: formatFormulaLabel(
      {
        ...formula,
        FormulaType: row.FormulaType,
        FormulaValue: row.FormulaValue,
        FormulaName: row.FormulaName,
      },
      { fallback: "" }
    ),
    RentId: toNum(row.RentId ?? rent.Id),
    RentPrice: row.RentPrice ?? rent.Price ?? null,
    RentPaymentFrequency:
      row.RentPaymentFrequency ?? rent.PaymentFrequency ?? null,
    RentIsActive: row.RentIsActive ?? rent.IsActive ?? null,
    IsAccumulative: toBool(row.IsAccumulative),
    IsActive: toBool(row.IsActive ?? true),
    StartDateTime: row.StartDateTime ?? row.startDateTime ?? null,
    FinishDateTime: row.FinishDateTime ?? row.finishDateTime ?? null,
    LocalDisplayNumber: toOptionalNum(row.LocalDisplayNumber),
  };
};

const asArray = (data) => (Array.isArray(data) ? data : data?.data ?? []);

class LocalMachineService {
  static async createLocalMachine(localMachine) {
    try {
      const response = await axios.post(API_URL, localMachine);
      const row = response.data?.localMachine ?? response.data;
      return normalizeRow(row);
    } catch (error) {
      console.error("Error al crear la relación Local-Máquina:", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  }

  static async createLocalMachineWithCounterConfig(payload) {
    try {
      const response = await axios.post(`${API_URL}/with-counter-config`, payload);
      const row = response.data?.localMachine ?? response.data;
      return normalizeRow(row);
    } catch (error) {
      console.error("Error al crear LocalMachine con configuración de contadores:", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  }

  static async moveLocalMachine(id, targetLocalId, EffectiveDate, LocalDisplayNumber = undefined) {
    try {
      const payload = { targetLocalId, EffectiveDate };
      if (LocalDisplayNumber !== undefined) {
        payload.LocalDisplayNumber = LocalDisplayNumber;
      }
      const { data } = await axios.post(`${API_URL}/${id}/move`, payload);
      return normalizeRow(data);
    } catch (error) {
      console.error("Error al mover la relación Local-Máquina:", {
        url: `${API_URL}/${id}/move`,
        sent: { targetLocalId, EffectiveDate, LocalDisplayNumber },
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async getMoveDateBounds(localMachineId) {
    try {
      const { data } = await axios.get(`${API_URL}/${localMachineId}/move-date-bounds`);
      return data || null;
    } catch (error) {
      console.error("Error obteniendo limites de fecha para mover LocalMachine:", {
        localMachineId,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      return null;
    }
  }

  static async updateLocalDisplayNumber(localMachineId, payload = {}) {
    try {
      const body = {
        LocalDisplayNumber: payload.LocalDisplayNumber ?? null,
        EffectiveDate: payload.EffectiveDate,
      };
      const { data } = await axios.patch(`${API_URL}/${localMachineId}/display-number`, body);
      return normalizeRow(data);
    } catch (error) {
      console.error("Error actualizando Nº en local:", {
        url: `${API_URL}/${localMachineId}/display-number`,
        sent: payload,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async discardFromDeposito(localMachineId, depositoLocalId, desechoLocalId, EffectiveDate) {
    try {
      const payload = {
        DepositoLocalId: depositoLocalId,
        DesechoLocalId: desechoLocalId,
        EffectiveDate,
      };

      const { data } = await axios.post(
        `${API_URL}/${localMachineId}/discard-from-deposito`,
        payload
      );

      return data;
    } catch (error) {
      console.error("LocalMachineService.discardFromDeposito - error:", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  }

  static async getLocalMachines() {
    try {
      const response = await axios.get(API_URL);
      return asArray(response.data).map(normalizeRow);
    } catch (error) {
      console.error("Error al obtener las relaciones Local-Máquina:", error);
      return [];
    }
  }

  static async getLocalMachineByLocal(localId) {
    try {
      const endpoint = `${API_URL}/byLocal/${localId}`;
      const response = await axios.get(endpoint);
      return asArray(response.data).map(normalizeRow);
    } catch (error) {
      console.error("Error al obtener las máquinas del local por LocalId:", error);
      return [];
    }
  }

  static async getActiveLocalMachineByLocal(localId) {
    try {
      const endpoint = `${API_URL}/byLocal/${localId}/active`;
      const response = await axios.get(endpoint);
      return asArray(response.data).map(normalizeRow);
    } catch (error) {
      console.error("Error al obtener máquinas ACTIVAS del local:", error);
      return [];
    }
  }

  static async getInactiveLocalMachineByLocal(localId) {
    try {
      const endpoint = `${API_URL}/byLocal/${localId}/inactive`;
      const response = await axios.get(endpoint);
      return asArray(response.data).map(normalizeRow);
    } catch (error) {
      console.error("Error al obtener máquinas INACTIVAS del local:", error);
      return [];
    }
  }

  static async getLocalMachineById(id) {
    try {
      const endpoint = `${API_URL}/${id}`;
      const response = await axios.get(endpoint);
      const row = Array.isArray(response.data) ? response.data[0] : response.data;
      return row ? normalizeRow(row) : null;
    } catch (error) {
      console.error("Error al obtener la relación Local-Máquina por ID:", error);
      return null;
    }
  }

  static async updateLocalMachine(id, { FormulaId = null, Formula = null, IsAccumulative = false, EffectiveDate }) {
    try {
      const body = { FormulaId, Formula, IsAccumulative, EffectiveDate };
      const { data } = await axios.post(`${API_URL}/${id}/updateLocalMachine`, body);
      return normalizeRow(data);
    } catch (error) {
      console.error("Error cambiando configuración de Local-Máquina:", {
        url: `${API_URL}/${id}/updateLocalMachine`,
        sent: { FormulaId, Formula, IsAccumulative, EffectiveDate },
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async updateLocalMachineRent(id, { Price, PaymentFrequency, EffectiveDate }) {
    try {
      const payload = {
        Price,
        PaymentFrequency,
        EffectiveDate,
      };
      const { data } = await axios.post(`${API_URL}/${id}/updateRent`, payload);
      return normalizeRow(data);
    } catch (error) {
      console.error("Error cambiando configuración de RENT de Local-Máquina:", {
        url: `${API_URL}/${id}/updateRent`,
        sent: { Price, PaymentFrequency, EffectiveDate },
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async deleteLocalMachine(id) {
    try {
      const response = await axios.delete(`${API_URL}/${id}`);
      if (response.status === 200) {
        return {
          ok: true,
          profitPercentageDeleted: Boolean(response.data?.profitPercentageDeleted),
        };
      }
      return { ok: response.status === 204, profitPercentageDeleted: false };
    } catch (error) {
      if (error?.response?.status === 404) {
        return { ok: true, profitPercentageDeleted: false };
      }
      console.error("Error al eliminar la relación Local-Máquina:", error);
      return { ok: false, profitPercentageDeleted: false };
    }
  }
}

export default LocalMachineService;
